import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AppState } from 'react-native';
import { AppLifecycle } from 'react-native-applifecycle';
import NetInfo from "@react-native-community/netinfo";
import { setGoHome, setInitAppLogout, setIsOnline, setServiceStatus } from '../Redux/Reducers/appConfigReducer';
import { makeLayoutApiCalls, triggerRepitativeApiCalls, callAppHealthService } from '../js/fetchAppConfigs';
import { requestNotificationPermission } from '../js/requestPermissions/notifications/requestNotificationPermission';
import { checkAppHealthService } from '../Redux/Actions/appHealthService';
import { getGuestItineraryList } from '../Redux/Actions/guestItineraryAction';
import { updateFCMTokenForRegisterDevice } from '../Redux/Actions/registerDeviceAction';
import { wipeGuestData } from '../js/wipeGuestData';
import { setProceedLoginToTheApp, setRenderLoader } from '../Redux/Actions/appConfigAction';
import { useNavigation } from '@react-navigation/native';
import { LAUNCHED } from '../config/Constant';
import { HOME_PAGE } from '../navigation/StackNavigator/routes';
import { sendAnalytics } from '../Redux/Actions/analyticsAction';
import { linkToRoom } from '../Redux/Actions/roomLinkingAction';
import { setCurrentWidget } from '../Redux/Reducers/deviceConfigReducer';
import { clearSelectedItems } from '../Redux/Reducers/guestRequestReducer';
import { guestJourneyNotification } from '../Redux/Actions/gjNotificationAction';

/**
 * Custom hook to manage app lifecycle events
 * Handles app state changes, network connectivity, and periodic tasks
 */
export const useAppLifecycle = (innspireChatRef) => {
    const dispatch = useDispatch();
    const navigation = useNavigation(); 

    const appState = useRef(AppState.currentState);
    const renderAppTimeout = useRef(null);

    const {appSessionToken, fcmToken, renderApp, goHome, initAppLogout, proceedLoginToTheApp, isBookingFound, bookingData, isLoggedIn} = useSelector(
        state => ({
            appSessionToken: state.appConfigSlice.appSessionToken,
            renderApp: state.appConfigSlice.renderApp,
            fcmToken: state.appConfigSlice.fcmToken,
            goHome: state.appConfigSlice.goHome,
            initAppLogout: state.appConfigSlice.initAppLogout,
            proceedLoginToTheApp: state.appConfigSlice.proceedLoginToTheApp,
            isBookingFound: state.checkInSlice.bookingStatus.isBookingFound,
            bookingData: state.checkInSlice.bookingStatus.bookingData,
            isLoggedIn: state.appConfigSlice.isLoggedIn,
        }),
        shallowEqual
    );

    const renderAppRef = useRef(renderApp);

    // Keep renderAppRef in sync
    useEffect(() => {
        renderAppRef.current = renderApp;
    }, [renderApp]);





    // Handle app logout
    useEffect(() => {
        if (initAppLogout) {
            // Booking and pre-checkin state is handled by useBookingState hook
            dispatch(setInitAppLogout(false));
            dispatch(wipeGuestData({ innspireChatRef }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initAppLogout]);


    /**
     * Handle room linking callback
     */
    const handleConnectCallback = (status, errorMessage) => {
        dispatch(setRenderLoader(false));
    };

    // Handle login flow
    useEffect(() => {
        if (proceedLoginToTheApp.init) {
            dispatch(setRenderLoader(true, "Connecting to your room..."));
            dispatch(linkToRoom(proceedLoginToTheApp.guestDetails, handleConnectCallback));
            dispatch(setProceedLoginToTheApp(false, {}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proceedLoginToTheApp]);




    // Fetch itinerary when booking or logged in
    useEffect(() => {
        if (isBookingFound) {
            dispatch(checkAppHealthService());
            dispatch(getGuestItineraryList());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBookingFound]);


    useEffect(() => {
        if (isLoggedIn) {
            dispatch(getGuestItineraryList());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);



    /**
* Navigate to home page
*/
    const handleGoHome = () => {
        dispatch(setGoHome(false));
        dispatch(setCurrentWidget({}));
        dispatch(clearSelectedItems());
        dispatch(guestJourneyNotification());

        const navigationState = navigation.getState();
        const stackScreens = navigationState.routes;
        console.log("current navigation stack: ", stackScreens);

        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            const currentRoute = stackScreens[stackScreens.length - 1]?.name;
            if (currentRoute === HOME_PAGE) {
                console.log("Already on HOME_PAGE, no navigation needed.");
                return;
            }

            navigation.reset({
                index: 0,
                routes: [{ name: HOME_PAGE }],
            });
        }
    };

    // Handle go home action
    useEffect(() => {
        if (goHome) {
            handleGoHome();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goHome]);



    // Update FCM token after delay
    useEffect(() => {
        setTimeout(() => {
            if (fcmToken && appSessionToken) {
                updateFCMTokenForRegisterDevice(appSessionToken, fcmToken);
            }
        }, 3000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fcmToken, appSessionToken]);



    // Track app launch analytics and timeout
    useEffect(() => {
        if (renderApp) {
            if (renderAppTimeout.current) {
                clearTimeout(renderAppTimeout.current);
                renderAppTimeout.current = null;
            }
            dispatch(sendAnalytics("APP_LAUNCH", LAUNCHED, {}));
        } else {
            renderAppTimeout.current = setTimeout(() => {
                dispatch(setServiceStatus({ hasError: true, errorMessage: "Unable to reach server. Please try again." }));
                dispatch(sendAnalytics("APP_LAUNCH", "SERVER_TIMEDOUT", {}));
            }, 40000);
        }

        return () => {
            if (renderAppTimeout.current) {
                clearTimeout(renderAppTimeout.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderApp]);




    /**
     * Handle app state changes (foreground/background)
     */
    const handleAppStateChange = async (nextAppState) => {
        console.log("App State Changed: ", nextAppState);

        if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
            // App came to foreground
            if (!fcmToken) {
                console.log('App came to foreground — rechecking notification permission');
                dispatch(requestNotificationPermission());
            }
        }

        appState.current = nextAppState;
    };

    /**
     * Setup app lifecycle and network monitoring
     */
    useEffect(() => {
        // App state listener
        const appStateListener = AppLifecycle.addEventListener('change', handleAppStateChange);

        // Network connectivity listener
        let lastConnectivityStatus = null;
        const connectivityListener = NetInfo.addEventListener((state) => {
            const online = state.isConnected && state.isInternetReachable;
            console.log("Internet connectivity changed:", online);

            if (typeof online === 'boolean' && online !== lastConnectivityStatus) {
                dispatch(setIsOnline(online));
                lastConnectivityStatus = online;

                if (online) {
                    if (!renderAppRef.current) {
                        dispatch(makeLayoutApiCalls({ isOnline: lastConnectivityStatus }));
                    } else {
                        dispatch(triggerRepitativeApiCalls());
                        dispatch(callAppHealthService());
                    }
                } else {
                    if (!renderAppRef.current) {
                        dispatch(makeLayoutApiCalls({ isOnline: lastConnectivityStatus }));
                    }
                }
            }
        });

        // Periodic layout refresh (every 10 minutes)
        const refreshLayoutInterval = setInterval(() => {
            if (lastConnectivityStatus === true) {
                dispatch(makeLayoutApiCalls({ avoidDataReset: true, isOnline: lastConnectivityStatus }));
            }
        }, 10 * 60 * 1000);

        // Cleanup listeners on unmount
        return () => {
            connectivityListener();
            appStateListener.remove();
            clearInterval(refreshLayoutInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};
